import { inject, Injectable } from '@angular/core';
import { getSparte, PreisIndikation, ProduktAttribute, Sparte, TarifOption } from '@mwe/models';
import { ProductCategoryEnum, productCategoryEnumToName, ProductPriceIndicationCategory } from '@mwe/constants';
import { TranslateService } from '@ngx-translate/core';
import { NewClientStateService } from '../new-client/new-client-state.service';

//TODO: this service should be put in the backend imho
@Injectable({
  providedIn: 'root',
})
export class PriceIndicationMappingService {
  private translateService = inject(TranslateService);
  private newClientStateService = inject(NewClientStateService);

  mapPriceIndicationsForChargingCardProducts(priceIndications: PreisIndikation[]): PreisIndikation[] {
    priceIndications.forEach(p => {
      if (p.sparte === ProductPriceIndicationCategory.TANKE) {
        p.sparte = Sparte.Emobility;
      }
    });
    return priceIndications;
  }

  mapMvnoIndicationsForInternetProducts(priceIndications: PreisIndikation[]): PreisIndikation[] {
    return priceIndications.map(priceIndication => {
      const mappedProductAttributes = this.mapMvnoProductAttributes(priceIndication);

      return {
        ...priceIndication,
        arbeitspreisLabel: priceIndication?.preisbestandteile?.preise?.[0]?.bildunterschrift,
        arbeitspreisZahlbar: priceIndication?.bewerbung?.grundpreis,
        arbeitspreisSubtext: priceIndication?.grundpreisSubtext ?? priceIndication.grundpreisIconInfoText,
        arbeitspreisUnit: priceIndication?.preisbestandteile?.preise?.[0]?.einheit,
        internet: {
          ...priceIndication?.internet,
          tvKosten: priceIndication?.internet?.tvKosten?.filter(tvCost => tvCost.kostenArt === 'monatlich'),
        },
        preisbestandteile: {
          preise: priceIndication?.preisbestandteile?.preise,
          produktAttribute: priceIndication?.preisbestandteile?.produktAttribute,
          mvnoFrontProduktAttribute: mappedProductAttributes,
        },
      };
    });
  }

  mapPriceIndicationsForInternetProducts(priceIndications: PreisIndikation[]): PreisIndikation[] {
    const internetPriceIndikations = [];
    const separatedTraiffOptionKey = [];
    const internetTariffsAvailable = this.newClientStateService.productsForSelection?.produkte
      .filter(product => product.sparte === Sparte.Internet)
      .map(product => product.tarif.ISUTarifKey);

    priceIndications.forEach(priceIndication => {
      if (internetTariffsAvailable?.includes(priceIndication.tarif.ISUTarifKey)) {
        priceIndication.sparte = getSparte(productCategoryEnumToName(ProductCategoryEnum.INTERNET));
        if (priceIndication.tarif.tarifOptionen) {
          priceIndication.tarif.tarifOptionen.forEach(to => {
            if (!separatedTraiffOptionKey.includes(to.option)) {
              const seaparatedIndication = this.createIPTVorVOIPIndication(to);
              if (seaparatedIndication) {
                internetPriceIndikations.push(seaparatedIndication);
                separatedTraiffOptionKey.push(seaparatedIndication.tarif.ISUTarifKey);
              }
            }
          });
        }
      }
    });

    priceIndications.forEach(priceIndication => {
      if (priceIndication.tarif.tarifOptionen) {
        priceIndication.tarif.tarifOptionen = priceIndication.tarif.tarifOptionen.filter(
          to => !separatedTraiffOptionKey.includes(to.option),
        );
      }
    });

    return priceIndications.concat(internetPriceIndikations);
  }

  private mapMvnoProductAttributes(priceIndication: PreisIndikation): ProduktAttribute[] {
    const isMobileInternet = priceIndication?.sparte === 'mobiles internet';

    const filteredAttributes =
      priceIndication?.preisbestandteile?.produktAttribute?.filter(
        produktAttribut => produktAttribut?.name === 'Max. Download:' || produktAttribut?.name === 'Max. Upload:',
      ) || [];

    if (priceIndication?.internet?.vertragsbindung) {
      filteredAttributes.push({
        name: 'Mindestvertragsdauer:',
        kurzWert: priceIndication.internet.vertragsbindung + ' Monate',
      } as ProduktAttribute);
    }

    if (priceIndication?.internet?.router?.name) {
      filteredAttributes.push({
        name: 'Router:',
        kurzWert: priceIndication.internet.router.name,
        infoTextTitle: 'Router',
        infoTextDescription: 'router-modal-info',
        routerModalData: {
          routerName: priceIndication.internet.router.bezeichnung,
          beschreibung: priceIndication?.internet?.router?.beschreibung,
          aufzaehlungspunkte: priceIndication?.internet?.router?.aufzaehlungspunkte,
          preisTexte: priceIndication?.internet?.router?.preisTexte,
          imageName: isMobileInternet ? 'G5B2full.png' : 'FritzBox.jpg',
        },
      } as ProduktAttribute);
    }

    if (priceIndication?.internet?.router?.preisInstallation && !isMobileInternet) {
      filteredAttributes.push({
        name: 'Installation:',
        kurzWert: priceIndication?.internet?.router?.preisInstallation + ' EUR',
      } as ProduktAttribute);
    }

    return filteredAttributes;
  }

  private createIPTVorVOIPIndication(to: TarifOption): PreisIndikation {
    const ipTvTariff = ['WEBIPTVLIGHT', 'WEBIPTVPREMIUM'];
    const voipTariff = ['WEBTELEU500', 'WEBTEL\u00d6500'];
    let category;
    if (ipTvTariff.includes(to.option)) {
      category = productCategoryEnumToName(ProductCategoryEnum.IPTV);
    } else if (voipTariff.includes(to.option)) {
      category = productCategoryEnumToName(ProductCategoryEnum.VOIP);
    }
    if (category) {
      const apAttributes = to.preise.find(p => p.deltaBasis?.toLowerCase() === 'arbeitspreis');
      const gpAttributes = to.preise.find(p => p.deltaBasis?.toLowerCase() === 'grundpreis');
      const ipAttributes = to.preise.find(p => p.deltaBasis?.toLowerCase() === 'anschlusspreis');
      return {
        sparte: getSparte(category),
        arbeitspreis: apAttributes?.delta,
        arbeitspreisUnit: apAttributes?.deltaUnit,
        grundpreis: gpAttributes?.delta,
        grundpreisUnit: gpAttributes?.deltaUnit,
        anschlusspreis: ipAttributes?.delta,
        anschlusspreisUnit: ipAttributes?.deltaUnit,
        tarif: {
          ISUTarifKey: to.option,
          tarifKey: to.cmsOptionId,
          tarifName: to?.optionLabel ?? '',
          tarifOptionen: this.populateTariffOptions(to.tarifOptionen),
        },
        boni: [],
        preisbestandteile: null, // TODO provide mapping
      };
    }
    return null;
  }

  private populateTariffOptions(tariffOptions: TarifOption[]): TarifOption[] {
    const resultingTariffOptions = [];
    tariffOptions?.forEach(to => {
      const gpAttributes = to.preise.find(p => p.deltaBasis === 'grundpreis');
      const ipAttributes = to.preise.find(p => p.deltaBasis === 'anschlusspreis');
      to.deltaBasis = gpAttributes?.deltaBasis;
      to.deltaUnit = gpAttributes?.deltaUnit;
      if (ipAttributes && Number(ipAttributes.delta.replace(',', '.')) > 0) {
        to.installationLabel = this.translateService.instant('tariff.details.installationPriceIptv');
        to.installationPrice = ipAttributes.delta;
        to.installationBasis = ipAttributes.deltaBasis;
        to.installationUnit = ipAttributes.deltaUnit;
      }
      to.delta = gpAttributes?.delta;
      resultingTariffOptions.push({ ...to });
    });

    return resultingTariffOptions;
  }
}
